import {
    Camera,
    ChevronDown,
    ChevronRight,
    Heart,
    ImagePlus,
    MapPin,
    Mic,
    Package,
    ScanLine,
    Search,
    ShoppingCart,
    UserRound,
    X,
} from "lucide-react";
import {
    ChangeEvent,
    FormEvent,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useNavigate } from "react-router-dom";
import {
    mobileHomeCategories,
    mobileHomeServices,
    MobileHomeCategoryId,
    MobileHomeServiceId,
} from "./MobileHomeNavbarData";
import { useAppDispatch, useAppSelector } from "../../../../Redux/hooks";
import { useAuth } from "../../../../context/AuthContext";
import {
    getAllAddresses,
    setDefaultAddress,
} from "../../../../Redux/customer-address/slice";
import { setDeliveryLocation } from "../../../../Redux/delivery-location/slice";
import {
    requestCurrentPosition,
    reverseGeocodeCoordinates,
} from "../../../../utility/location";
import MobileAddressSheet from "../../HomeNavbar/MobileAddressSheet";
import { MobileSavedAddress } from "../../HomeNavbar/MobileAddressSheetData";
import "./MobileHomeNavbar.css";

const MobileHomeNavbar = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { user, isAuthenticated } = useAuth();

    const imageInputRef = useRef<HTMLInputElement>(null);
    const qrInputRef = useRef<HTMLInputElement>(null);

    const [activeService, setActiveService] = useState<MobileHomeServiceId>("shopping");
    const [activeCategory, setActiveCategory] = useState<MobileHomeCategoryId>("men");
    const [searchValue, setSearchValue] = useState("");
    const [profileOpen, setProfileOpen] = useState(false);
    const [addressSheetOpen, setAddressSheetOpen] = useState(false);
    const [utilityMessage, setUtilityMessage] = useState("");

    const { location } = useAppSelector((state) => state.deliveryLocation);
    const { addresses } = useAppSelector((state) => state.customerAddress);

    const deliveryAddress = useMemo(() => {
        if (location?.displayName) return location.displayName;
        if (location?.label) return location.label;
        return "Select delivery location";
    }, [location]);

    const userName = useMemo(() => {
        if (!isAuthenticated) return "Guest";
        return user?.fullName || user?.name || (user?.email ? user.email.split("@")[0] : "My Account");
    }, [user, isAuthenticated]);

    const userId = useMemo(() => {
        if (!isAuthenticated) return "Not signed in";
        return user?.myReferralId || user?._id?.slice(-8).toUpperCase() || "—";
    }, [user, isAuthenticated]);

    const selectedCategory = useMemo(
        () => mobileHomeCategories.find((c) => c.id === activeCategory) || mobileHomeCategories[0],
        [activeCategory],
    );

    const searchPlaceholder = useMemo(() => {
        if (activeService === "quick") return "Search groceries & daily needs";
        if (activeService === "food") return "Search food, dishes & restaurants";
        if (activeService === "stores") return "Search nearby stores";
        return `Search ${selectedCategory.shortLabel.toLowerCase()} & more`;
    }, [activeService, selectedCategory.shortLabel]);

    const savedAddresses: MobileSavedAddress[] = useMemo(
        () =>
            (addresses || []).map((addr: any) => ({
                id: addr._id || addr.id,
                tag: addr.addressType || "Home",
                name: addr.fullName || "Address",
                phone: addr.phoneNumber || "",
                address: [
                    addr.addressLine1,
                    addr.addressLine2,
                    addr.city,
                    addr.state,
                    addr.pincode,
                ]
                    .filter(Boolean)
                    .join(", "),
                isDefault: Boolean(addr.defaultAddress),
            })),
        [addresses],
    );

    useEffect(() => {
        if (isAuthenticated) {
            dispatch(getAllAddresses());
        }
    }, [dispatch, isAuthenticated]);

    useEffect(() => {
        if (!utilityMessage) return;
        const timeout = setTimeout(() => setUtilityMessage(""), 3200);
        return () => clearTimeout(timeout);
    }, [utilityMessage]);

    const openSearchPage = () => {
        const value = searchValue.trim();
        navigate(value ? `/Search?q=${encodeURIComponent(value)}` : "/Search");
    };

    const handleSearchSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        openSearchPage();
    };

    const handleImageSelect = (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;
        setUtilityMessage(`Image selected: ${file.name}`);
        event.target.value = "";
    };

    const handleVoiceSearch = () => {
        const SpeechRecognition =
            (window as any).SpeechRecognition ||
            (window as any).webkitSpeechRecognition;

        if (!SpeechRecognition) {
            setUtilityMessage("Voice search is not supported on this browser.");
            return;
        }

        const recognition = new SpeechRecognition();
        recognition.lang = "en-IN";
        recognition.interimResults = false;
        recognition.maxAlternatives = 1;

        recognition.onstart = () => setUtilityMessage("Listening… speak your search.");
        recognition.onresult = (event: any) => {
            const transcript = event.results?.[0]?.[0]?.transcript || "";
            if (transcript) {
                setSearchValue(transcript);
                setUtilityMessage(`Voice search: ${transcript}`);
            }
        };
        recognition.onerror = () => setUtilityMessage("Could not hear you. Try again.");
        recognition.start();
    };

    const handleQrImageSelect = async (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        try {
            const BarcodeDetector = (window as any).BarcodeDetector;
            if (!BarcodeDetector) {
                setUtilityMessage("QR image captured. Scanner support depends on the device browser.");
                return;
            }
            const bitmap = await createImageBitmap(file);
            const detector = new BarcodeDetector({ formats: ["qr_code"] });
            const codes = await detector.detect(bitmap);
            if (codes.length > 0) {
                setUtilityMessage(`QR detected: ${codes[0].rawValue || "Code found"}`);
            } else {
                setUtilityMessage("No QR code found in the image.");
            }
        } catch {
            setUtilityMessage("Unable to read that QR image.");
        } finally {
            event.target.value = "";
        }
    };

    const handleSavedAddressSelect = (address: MobileSavedAddress) => {
        const raw = (addresses || []).find(
            (addr: any) => (addr._id || addr.id) === address.id
        );

        if (raw?._id && !raw.defaultAddress) {
            dispatch(setDefaultAddress(raw._id)).then(() => {
                dispatch(getAllAddresses());
            });
        }

        dispatch(
            setDeliveryLocation({
                label: raw?.addressType ?? address.tag,
                area: raw?.addressLine1,
                city: raw?.city,
                state: raw?.state,
                pincode: raw?.pincode,
                displayName: address.address,
                latitude: raw?.latitude,
                longitude: raw?.longitude,
            })
        );

        setAddressSheetOpen(false);
        setUtilityMessage("Delivery address updated.");
    };

    const handleUseCurrentLocation = async () => {
        if (!navigator.geolocation) {
            setUtilityMessage("Current location is not supported on this device.");
            return;
        }

        setUtilityMessage("Getting your current location…");

        try {
            const coords = await requestCurrentPosition();
            const details = await reverseGeocodeCoordinates(coords.latitude, coords.longitude);
            const resolvedAddress =
                details.displayName ||
                [details.area, details.city, details.state, details.pincode]
                    .filter(Boolean)
                    .join(", ");

            dispatch(
                setDeliveryLocation({
                    label: "Current Location",
                    area: details.area,
                    city: details.city,
                    state: details.state,
                    pincode: details.pincode,
                    displayName: resolvedAddress,
                    latitude: coords.latitude,
                    longitude: coords.longitude,
                })
            );

            setAddressSheetOpen(false);
            setUtilityMessage("Current location selected.");
        } catch {
            setUtilityMessage("Location permission was not available. Choose a saved address instead.");
        }
    };

    const handleCategoryClick = (categoryId: MobileHomeCategoryId) => {
        setActiveCategory(categoryId);
        const cat = mobileHomeCategories.find((c) => c.id === categoryId);
        if (cat) {
            navigate(`/Categories/${encodeURIComponent(cat.label)}`);
        }
    };

    const handleExplore = () => {
        navigate(`/Categories/${encodeURIComponent(selectedCategory.label)}`);
    };

    return (
        <header className={`salar-mobile-home-nav ${selectedCategory.themeClass}`}>
            <div className="salar-mobile-home-safe">
                <div className="salar-mobile-home-user-row">
                    <button
                        type="button"
                        className="salar-mobile-home-user"
                        onClick={() => setProfileOpen((c) => !c)}
                        aria-expanded={profileOpen}
                    >
                        <strong>{userName}</strong>
                        <small>ID: {userId}</small>
                    </button>

                    <button
                        type="button"
                        className={`salar-mobile-home-profile ${profileOpen ? "active" : ""}`}
                        onClick={() => setProfileOpen((c) => !c)}
                        aria-label="Open profile menu"
                        aria-expanded={profileOpen}
                    >
                        <UserRound size={22} strokeWidth={1.9} />
                    </button>
                </div>

                {profileOpen && (
                    <div className="salar-mobile-home-profile-panel">
                        <div className="salar-mobile-home-profile-summary">
                            <span>
                                <UserRound size={20} strokeWidth={1.9} />
                            </span>
                            <div>
                                <strong>{userName}</strong>
                                <small>{userId}</small>
                            </div>
                        </div>

                        <div className="salar-mobile-home-profile-actions">
                            <button type="button" onClick={() => { setProfileOpen(false); navigate("/Orders"); }}>
                                <Package size={17} />
                                <span>My Orders</span>
                                <ChevronRight size={15} />
                            </button>

                            <button type="button" onClick={() => { setProfileOpen(false); navigate("/wishlist"); }}>
                                <Heart size={17} />
                                <span>Wishlist</span>
                                <ChevronRight size={15} />
                            </button>

                            <button type="button" onClick={() => { setProfileOpen(false); navigate("/Cart"); }}>
                                <ShoppingCart size={17} />
                                <span>My Cart</span>
                                <ChevronRight size={15} />
                            </button>
                        </div>
                    </div>
                )}

                <div className="salar-mobile-home-service-scroll">
                    {mobileHomeServices.map((service) => (
                        <button
                            type="button"
                            key={service.id}
                            className={activeService === service.id ? "active" : ""}
                            onClick={() => setActiveService(service.id)}
                        >
                            <span className="salar-mobile-home-service-icon">
                                {service.icon}
                            </span>

                            <span className="salar-mobile-home-service-copy">
                                <strong>{service.label}</strong>
                                <small>{service.caption}</small>
                            </span>
                        </button>
                    ))}
                </div>

                <button
                    type="button"
                    className="salar-mobile-home-address"
                    onClick={() => setAddressSheetOpen(true)}
                    aria-label="Select delivery address"
                    aria-expanded={addressSheetOpen}
                >
                    <span className="salar-mobile-home-address-icon">
                        <MapPin size={18} strokeWidth={2} />
                    </span>

                    <div>
                        <small>Deliver to</small>
                        <strong>{deliveryAddress}</strong>
                    </div>

                    <ChevronDown size={17} strokeWidth={2} />
                </button>

                <div className="salar-mobile-home-search-row">
                    <form className="salar-mobile-home-search" onSubmit={handleSearchSubmit}>
                        <Search size={21} strokeWidth={2} />

                        <input
                            type="search"
                            value={searchValue}
                            onPointerDown={(event) => {
                                event.preventDefault();
                                openSearchPage();
                            }}
                            onFocus={openSearchPage}
                            onChange={(e) => setSearchValue(e.target.value)}
                            placeholder={searchPlaceholder}
                            aria-label="Open Salar search"
                            autoComplete="off"
                        />

                        {searchValue && (
                            <button
                                type="button"
                                className="salar-mobile-home-search-clear"
                                onClick={() => setSearchValue("")}
                                aria-label="Clear search"
                            >
                                <X size={17} strokeWidth={2} />
                            </button>
                        )}

                        <button
                            type="button"
                            className="salar-mobile-home-search-utility"
                            onClick={() => imageInputRef.current?.click()}
                            aria-label="Search using an image"
                        >
                            <ImagePlus size={20} strokeWidth={1.8} />
                        </button>

                        <button
                            type="button"
                            className="salar-mobile-home-search-utility"
                            onClick={handleVoiceSearch}
                            aria-label="Search using microphone"
                        >
                            <Mic size={20} strokeWidth={1.9} />
                        </button>

                        <input
                            ref={imageInputRef}
                            className="salar-mobile-home-hidden-input"
                            type="file"
                            accept="image/*"
                            onChange={handleImageSelect}
                        />
                    </form>

                    <button
                        type="button"
                        className="salar-mobile-home-qr"
                        onClick={() => qrInputRef.current?.click()}
                        aria-label="Scan QR code"
                    >
                        <ScanLine size={23} strokeWidth={1.8} />
                    </button>

                    <input
                        ref={qrInputRef}
                        className="salar-mobile-home-hidden-input"
                        type="file"
                        accept="image/*"
                        capture="environment"
                        onChange={handleQrImageSelect}
                    />
                </div>

                {utilityMessage && (
                    <div className="salar-mobile-home-utility-message" aria-live="polite">
                        <Camera size={14} strokeWidth={1.9} />
                        <span>{utilityMessage}</span>
                    </div>
                )}

                <nav className="salar-mobile-home-category-scroll" aria-label="Shop by category">
                    {mobileHomeCategories.map((category) => (
                        <button
                            type="button"
                            key={category.id}
                            className={activeCategory === category.id ? "active" : ""}
                            onClick={() => handleCategoryClick(category.id)}
                        >
                            <span>{category.icon}</span>
                            <strong>{category.shortLabel}</strong>
                        </button>
                    ))}
                </nav>
            </div>

            <section className="salar-mobile-home-category-showcase">
                <div className="salar-mobile-home-showcase-heading">
                    <div>
                        <span>{selectedCategory.eyebrow}</span>
                        <h2>{selectedCategory.title}</h2>
                    </div>

                    <button type="button" aria-label={`Explore ${selectedCategory.label}`} onClick={handleExplore}>
                        Explore
                        <ChevronRight size={15} strokeWidth={2} />
                    </button>
                </div>

                <p>{selectedCategory.subtitle}</p>

                <div className="salar-mobile-home-offer-scroll">
                    {selectedCategory.offers.map((offer) => (
                        <article key={offer.id}>
                            <span className="salar-mobile-home-offer-emoji">
                                {offer.emoji}
                            </span>

                            <div>
                                <strong>{offer.title}</strong>
                                <small>{offer.caption}</small>
                            </div>

                            <b>{offer.badge}</b>
                        </article>
                    ))}
                </div>
            </section>

            <MobileAddressSheet
                open={addressSheetOpen}
                addresses={savedAddresses}
                selectedAddress={deliveryAddress}
                onClose={() => setAddressSheetOpen(false)}
                onSelectAddress={handleSavedAddressSelect}
                onUseCurrentLocation={handleUseCurrentLocation}
                onAddNewAddress={() => {
                    setAddressSheetOpen(false);
                    navigate("/ManageAddress");
                }}
            />
        </header>
    );
};

export default MobileHomeNavbar;
